import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { SupportTrip } from "domain/entities/support-trip";
import { SupportTripInputFilter } from "domain/models/support-trip-input-filter";
import { Logger } from "infra/logger/logger";
import supportTripRepository from "infra/repositories/support-trip-repository";

export const useSupportTripByFilter = (filter: SupportTripInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["supportTrips", filter],
    queryFn: async () => {
      try {
        const result = await supportTripRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreateSupportTripMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<SupportTrip>) => {
      const result = await supportTripRepository.create(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supportTrips"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);

          // Cria um objeto de erro com o array de erros como propriedade
          const customError = new Error("Erro na criação da viagem");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
          throw new Error("Erro desconhecido da API");
        }
      } else {
        logger.error("Erro desconhecido", error);
        throw error;
      }
    },
  });
};

export const useSupportTripById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["supportTrips", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("Id inválido");
        const result = await supportTripRepository.getById(id);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useUpdateSupportTripMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SupportTrip) => {
      const result = await supportTripRepository.update(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supportTrips"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};

export const useDeleteSupportTripMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (supportTrip: SupportTrip) => {
      if (!supportTrip?.id) throw new Error("ID inválido para exclusão");
      const result = await supportTripRepository.delete(supportTrip?.id);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supportTrips"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};
